import { useStatistics } from '../hooks/useStatistics';

/**
 * Statistics Dashboard Component
 * Displays governance statistics and metrics
 */
export function StatisticsDashboard({ onClose }) {
  const {
    statistics,
    loading,
    error,
    getCompletionRate,
    getMostActiveCategory,
    getParticipationRate
  } = useStatistics();

  if (loading) {
    return (
      <div className="t24-statistics-dashboard loading">
        <div className="dashboard-header">
          <h2>Governance Statistics</h2>
          {onClose && (
            <button className="dashboard-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          )}
        </div>
        <div className="dashboard-loading">
          <div className="t24-loading-spinner"></div>
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="t24-statistics-dashboard error">
        <div className="dashboard-header">
          <h2>Governance Statistics</h2>
          {onClose && (
            <button className="dashboard-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          )}
        </div>
        <div className="dashboard-error">
          <span className="error-icon">⚠️</span>
          <p>Failed to load statistics: {error}</p>
        </div>
      </div>
    );
  }

  const completionRate = getCompletionRate();
  const mostActive = getMostActiveCategory();
  const participationRate = getParticipationRate();

  return (
    <div className="t24-statistics-dashboard">
      <div className="dashboard-header">
        <h2>
          <span className="header-icon">📊</span>
          Governance Statistics
        </h2>
        {onClose && (
          <button className="dashboard-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        )}
      </div>

      <div className="dashboard-content">
        {/* Key Metrics */}
        <div className="stats-section key-metrics">
          <h3>Key Metrics</h3>
          <div className="metrics-grid">
            {/* Total Proposals */}
            <div className="metric-card">
              <div className="metric-icon">📝</div>
              <div className="metric-content">
                <div className="metric-value">
                  {Object.values(statistics.by_status || {}).reduce((sum, count) => sum + count, 0)}
                </div>
                <div className="metric-label">Total Proposals</div>
              </div>
            </div>

            {/* Active Proposals */}
            <div className="metric-card">
              <div className="metric-icon">🔥</div>
              <div className="metric-content">
                <div className="metric-value">{statistics.active_proposals || 0}</div>
                <div className="metric-label">Active Proposals</div>
              </div>
            </div>

            {/* Total Votes */}
            <div className="metric-card">
              <div className="metric-icon">🗳️</div>
              <div className="metric-content">
                <div className="metric-value">{statistics.total_votes || 0}</div>
                <div className="metric-label">Total Votes Cast</div>
              </div>
            </div>

            {/* Unique Voters */}
            <div className="metric-card">
              <div className="metric-icon">👥</div>
              <div className="metric-content">
                <div className="metric-value">{statistics.unique_voters || 0}</div>
                <div className="metric-label">Unique Voters</div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Metrics */}
        <div className="stats-section progress-metrics">
          <h3>Progress & Engagement</h3>
          <div className="progress-grid">
            {/* Completion Rate */}
            <div className="progress-card">
              <div className="progress-header">
                <span className="progress-icon">✅</span>
                <span className="progress-title">Completion Rate</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill completion"
                  style={{ width: `${completionRate}%` }}
                ></div>
              </div>
              <div className="progress-value">{completionRate}%</div>
            </div>

            {/* Participation Rate */}
            <div className="progress-card">
              <div className="progress-header">
                <span className="progress-icon">📊</span>
                <span className="progress-title">Participation Rate</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill participation"
                  style={{ width: `${participationRate}%` }}
                ></div>
              </div>
              <div className="progress-value">{participationRate}%</div>
            </div>
          </div>
        </div>

        {/* By Category */}
        {statistics.by_category && Object.keys(statistics.by_category).length > 0 && (
          <div className="stats-section category-stats">
            <h3>Proposals by Category</h3>
            <div className="category-list">
              {Object.entries(statistics.by_category)
                .sort((a, b) => b[1] - a[1])
                .map(([category, count]) => (
                  <div key={category} className="category-item">
                    <div className="category-label">
                      <span className={`category-badge ${category}`}>
                        {window.t24Governance.categories[category] || category}
                      </span>
                      {mostActive && mostActive.category === category && (
                        <span className="most-active-badge">🔥 Most Active</span>
                      )}
                    </div>
                    <div className="category-count">{count}</div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* By Status */}
        {statistics.by_status && Object.keys(statistics.by_status).length > 0 && (
          <div className="stats-section status-stats">
            <h3>Proposals by Status</h3>
            <div className="status-list">
              {Object.entries(statistics.by_status)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => (
                  <div key={status} className="status-item">
                    <div className="status-label">
                      <span className={`status-badge ${status}`}>
                        {window.t24Governance.statuses[status] || status}
                      </span>
                    </div>
                    <div className="status-bar">
                      <div
                        className="status-bar-fill"
                        style={{
                          width: `${(count / Object.values(statistics.by_status).reduce((sum, c) => sum + c, 0)) * 100}%`
                        }}
                      ></div>
                    </div>
                    <div className="status-count">{count}</div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
